import { createClient } from '@supabase/supabase-js';

const SUPABASE_URL = import.meta.env.VENDOR_SUPABASE_URL ?? 'http://localhost:54521';
// Deterministic local-dev demo anon JWT minted by the pinned Supabase CLI
// (package.json devDependency `supabase@2.100.1`) — reproduced verbatim by
// `pnpm exec supabase status -o env` (ANON_KEY). The previous literal here
// predated that CLI version's demo-key payload shape and no longer decoded
// against a freshly-started local stack (PostgREST returned PGRST301, "no
// suitable key"), so every anonymous kiosk RPC call
// (kiosk_display_settings/kiosk_featured_cards/kiosk_upcoming_events/
// kiosk_capture_lead) silently failed and fell back to defaults/empty state
// — invisible in the UI (those hooks fail open) until asserted against real
// seeded data. Fixed 2026-07-31; see PR for `test-health-flake-skips-kiosk`.
const SUPABASE_ANON_KEY =
  import.meta.env.VENDOR_SUPABASE_ANON_KEY ??
  'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZS1kZW1vIiwicm9sZSI6ImFub24iLCJleHAiOjE5ODM4MTI5OTZ9.CRXP1A7WOeoJeXxjNni43kdQwgnWNReilDMblYTn_I0';

/**
 * A PRODUCTION build with no `VENDOR_SUPABASE_URL` is a broken deploy, and it
 * must fail loudly rather than fall through to the local defaults above.
 *
 * **This happened, 2026-08-29.** The vendor#1050 SPA was built in a fresh git
 * worktree, which has no `.env` (it is gitignored, so a new worktree never gets
 * one). Both defaults applied silently, the build succeeded, and
 * `vendor.mytcgbinder.com` shipped pointing at `http://localhost:54521`. Every
 * signed-out visitor got an OAuth redirect to a machine that is not theirs.
 * The only signal was a `console.warn` — emitted in the VISITOR's browser, long
 * after the deploy, where nobody building or shipping would ever see it.
 *
 * The warn stays for dev, where falling back to the local stack is the whole
 * point. In a production build the same condition throws at module load, so the
 * app fails on the first import instead of half-working against a dead host —
 * and, because this module is imported by the entry chunk, a smoke-load of the
 * built bundle surfaces it before the deploy rather than after.
 *
 * Note `VENDOR_SUPABASE_ANON_KEY` is deliberately NOT part of this check. It has
 * a real fallback for local dev and a wrong key fails closed and visibly
 * (PostgREST `PGRST301`), unlike a wrong URL, which fails as a network error
 * that reads like an outage. Pair this with the env file: the vite root is
 * `apps/web/`, so the file that matters is `apps/web/.env`, NOT the repo root.
 */
if (!import.meta.env.VENDOR_SUPABASE_URL) {
  if (import.meta.env.PROD) {
    throw new Error(
      '[vendor] VENDOR_SUPABASE_URL is not set in a production build. This bundle ' +
        'would point at http://localhost:54521 and every visitor would fail to sign in. ' +
        'Set it in apps/web/.env (the vite root — NOT the repo root) and rebuild.',
    );
  }
  console.warn(
    '[vendor] VENDOR_SUPABASE_URL not set — defaulting to local supabase at http://localhost:54521',
  );
}

export const supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY, {
  auth: { persistSession: true, autoRefreshToken: true },
});

/**
 * Helper that wraps queries for multi-policy tables (binders, slab_binders, display_cases,
 * bins, card_instances, sealed_stock, etc.). RLS unions an anonymous-kiosk policy with a
 * member policy; without an explicit account_id predicate, a signed-in staff user can see
 * every public-browse row across shops. NEVER use plain `.from(table).select('*')` against
 * these tables — always go through `scoped(table, currentAccountId)`.
 *
 * See CLAUDE.md "Critical guardrail #1".
 */
export function scoped(table: string, accountId: string) {
  return supabase.from(table).select('*').eq('account_id', accountId);
}
